import { Fragment, ReactNode } from 'react';
import styles from './styles/RankingsTable.module.scss';
import Table, { TableCell, TableHeaderCell, TableLinkRow, TableRow } from '@/components/Table';
import TeamImage from 'components/TeamImage';
import Trend from '@/components/Trend';
import TextLink, { TextColorVariant, TextLinkSize } from './TextLink';
import cn from 'classnames';

export interface TeamRankingType {
  id: string;
  filteredPlace?: number; // todo fix in api so this isnt nullable
  team: {
    id?: string;
    image?: string;
    locationText?: string;
    name: string;
    webPath: string;
  };
  record: string;
  points?: number;
  strengthOfSchedule?: number;
  streak?: string;
  top100Record?: string;
  filteredChange?: number;
}

export default function RankingsTable<T extends TeamRankingType>({
  isHighlighted,
  teamRankings,
  injectAtIndex,
  hideHeader
}: {
  isHighlighted?: (teamRanking: T) => boolean;
  teamRankings: T[];
  injectAtIndex?: (index: number) => ReactNode;
  hideHeader?: boolean;
}) {
  const hasPoints = teamRankings.some(teamRanking => teamRanking.points);
  const hasSOS = teamRankings.some(teamRanking => teamRanking.strengthOfSchedule);
  const hasStreak = teamRankings.some(teamRanking => teamRanking.streak);
  const hasTop100 = teamRankings.some(teamRanking => teamRanking.top100Record);

  return (
    <Table
      className={styles.Table}
      header={
        !hideHeader && (
          <>
            <TableHeaderCell className={styles.PlaceColumn}>#</TableHeaderCell>
            <TableHeaderCell className={styles.TeamColumn}>Team</TableHeaderCell>
            <TableHeaderCell align="right">W-L</TableHeaderCell>
            {hasPoints && (
              <TableHeaderCell className={styles.HideSmall} align="right">
                Pts
              </TableHeaderCell>
            )}
            {hasSOS && (
              <TableHeaderCell className={styles.HideSmall} align="right">
                SOS
              </TableHeaderCell>
            )}
            {hasStreak && (
              <TableHeaderCell className={styles.HideSmall} align="right">
                Streak
              </TableHeaderCell>
            )}
            {hasTop100 && (
              <TableHeaderCell className={styles.HideSmall} align="right">
                Top 100
              </TableHeaderCell>
            )}
            <TableHeaderCell align="right">+/-</TableHeaderCell>
          </>
        )
      }
    >
      {teamRankings.map((teamRanking, index) => {
        const injected = injectAtIndex && injectAtIndex(index);

        return (
          <Fragment key={teamRanking.id}>
            {injected && (
              <TableRow>
                <TableCell className={styles.Injected} colSpan={8}>
                  {injected}
                </TableCell>
              </TableRow>
            )}
            <TableLinkRow highlighted={isHighlighted?.(teamRanking)} href={teamRanking.team.webPath}>
              <TableCell className={cn({ [styles.PlaceColumn]: hideHeader && index === 0 })}>{teamRanking.filteredPlace}</TableCell>
              <TableCell className={cn({ [styles.TeamColumn]: hideHeader && index === 0 })}>
                <div className={styles.TeamContainer}>
                  <TextLink href={teamRanking.team.webPath} color={TextColorVariant.BLACK} size={TextLinkSize.SMALL}>
                    <TeamImage className={styles.TeamImage} src={teamRanking.team.image} alt={teamRanking.team.name} />
                    {teamRanking.team.name}
                  </TextLink>
                  <span className={styles.TeamLocationText}>{teamRanking.team.locationText}</span>
                </div>
              </TableCell>
              <TableCell align="right">{teamRanking.record}</TableCell>
              {hasPoints && (
                <TableCell className={styles.HideSmall} align="right">
                  {teamRanking.points?.toFixed(3)}
                </TableCell>
              )}
              {hasSOS && (
                <TableCell className={styles.HideSmall} align="right">
                  {teamRanking.strengthOfSchedule ? teamRanking.strengthOfSchedule.toFixed(3) : 'N/A'}
                </TableCell>
              )}
              {hasStreak && (
                <TableCell className={styles.HideSmall} align="right">
                  {teamRanking.streak || '-'}
                </TableCell>
              )}
              {hasTop100 && (
                <TableCell className={styles.HideSmall} align="right">
                  {teamRanking.top100Record}
                </TableCell>
              )}
              <TableCell align="right">
                <Trend value={teamRanking.filteredChange || 0} />
              </TableCell>
            </TableLinkRow>
          </Fragment>
        );
      })}
    </Table>
  );
}
